import { useState, useEffect, useCallback } from "react";
import { Slack } from "lucide-react";
import { useStore } from "../../store/useStore";
import { isNativeShell } from "../../lib/native/nativeShell";
import { addNativeResumeListener, openInNativeBrowser } from "../../lib/native/nativeBrowser";

export default function SlackConnectionPanel() {
  const [status, setStatus] = useState<"loading" | "connected" | "disconnected">("loading");
  const [connecting, setConnecting] = useState(false);
  const session = useStore((s) => s.session);

  const checkStatus = useCallback(async () => {
    if (!session?.access_token) return;
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/slack-oauth`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ action: "status" }),
        }
      );
      const data = await res.json();
      setStatus(data.connected ? "connected" : "disconnected");
    } catch {
      setStatus("disconnected");
    }
  }, [session?.access_token]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // The Slack consent runs in an in-app browser on native; re-check once the user is back.
  useEffect(() => {
    if (!isNativeShell()) return undefined;
    const cleanup = addNativeResumeListener(() => void checkStatus());
    return () => {
      void cleanup.then((remove) => remove());
    };
  }, [checkStatus]);

  // Check for ?slack=connected query param on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("slack") === "connected") {
      setStatus("connected");
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleConnect = async () => {
    if (!session?.access_token) return;
    setConnecting(true);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/slack-oauth?action=authorize`,
        {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }
      );
      const data = await res.json();
      if (data.url) {
        if (isNativeShell()) {
          // Navigating the WebView would leave the bundled app.
          await openInNativeBrowser({ url: data.url, onFinished: () => void checkStatus() });
        } else {
          window.location.href = data.url;
        }
      }
    } catch (err) {
      console.error("Failed to start Slack OAuth:", err);
    }
    setConnecting(false);
  };

  const handleDisconnect = async () => {
    if (!session?.access_token) return;
    try {
      await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/slack-oauth`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ action: "disconnect" }),
        }
      );
      setStatus("disconnected");
    } catch (err) {
      console.error("Failed to disconnect Slack:", err);
    }
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Slack className="w-5 h-5 text-muted-foreground" />
        <h3 className="text-sm font-medium text-foreground">
          Slack Integration
        </h3>
      </div>
      <p className="text-xs text-muted-foreground">
        React with a :brain: emoji on any Slack message to create a task.
      </p>

      {status === "loading" ? (
        <div className="text-xs text-muted-foreground">Checking connection...</div>
      ) : status === "connected" ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-success" />
            <span className="text-sm text-foreground">Connected to Slack</span>
          </div>
          <button
            onClick={handleDisconnect}
            className="text-xs text-destructive hover:text-destructive/80 transition-colors"
          >
            Disconnect
          </button>
        </div>
      ) : (
        <button
          onClick={handleConnect}
          disabled={connecting}
          className="btn-secondary flex items-center gap-2 text-sm"
        >
          <Slack className="w-4 h-4" />
          {connecting ? "Connecting..." : "Connect Slack"}
        </button>
      )}
    </div>
  );
}
